/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  introductionSectionTitle: {
    id: 'resources.introduction.title',
    defaultMessage: 'Introducing web chat'
  },
  introductionSectionSubTitle: {
    id: 'resources.introduction.subtitle',
    defaultMessage: 'Everything to get started'
  },
  webChatOverviewTileTitle: {
    id: 'resources.webChatOverviewTile.title',
    defaultMessage: 'Web chat overview'
  },
  webChatOverviewTileText: {
    id: 'resources.webChatOverviewTile.text',
    defaultMessage:
      'Learn about {productName} web chat, why you should use it, and what you can do with it.'
  },
  embedWebChatTileTitle: {
    id: 'resources.embedWebChatTile.title',
    defaultMessage: 'Embedding the web chat on your page'
  },
  embedWebChatTileText: {
    id: 'resources.embedWebChatTile.text',
    defaultMessage:
      'Learn how web chat can be deployed in minutes using our auto-generated embed script.'
  },
  addingLiveAgentTileTitle: {
    id: 'resources.addingLiveAgentTile.title',
    defaultMessage: 'Adding live agent support'
  },
  addingLiveAgentTileText: {
    id: 'resources.addingLiveAgentTile.text',
    defaultMessage:
      'Learn how to integrate web chat with your live agent tool of choice.'
  },
  outOfTheBoxSectionTitle: {
    id: 'resources.outOfTheBoxSection.title',
    defaultMessage: 'Out-of-the-box'
  },
  outOfTheBoxSectionSubTitle: {
    id: 'resources.outOfTheBoxSection.subtitle',
    defaultMessage: 'Easy to implement'
  },
  styleWebChatTileTitle: {
    id: 'resources.styleWebChatTile.title',
    defaultMessage: 'Style your web chat'
  },
  styleWebChatTileText: {
    id: 'resources.styleWebChatTile.text',
    defaultMessage:
      'Set up your style and branding preferences so web chat matches your preferred look and feel.'
  },
  setUpHomeScreenTileTitle: {
    id: 'resources.setUpHomeScreenTile.title',
    defaultMessage: 'Set up Home Screen'
  },
  setUpHomeScreenTileText: {
    id: 'resources.setUpHomeScreenTile.text',
    defaultMessage:
      'Give your users a great start to their conversation by setting up your Home Screen.'
  },
  setUpSuggestionTileTitle: {
    id: 'resources.setUpSuggestionTile.title',
    defaultMessage: 'Set up Suggestions'
  },
  setUpSuggestionTileText: {
    id: 'resources.setUpSuggestionTile.text',
    defaultMessage:
      'Help ensure your users can find the help they need by setting up Suggestions.'
  },
  richCustomizationSectionTitle: {
    id: 'resources.richCustomizationSection.title',
    defaultMessage: 'Rich customization'
  },
  richCustomizationSectionSubTitle: {
    id: 'resources.richCustomizationSection.subtitle',
    defaultMessage: 'Adaptable to your needs'
  },
  developmentOverviewTileTitle: {
    id: 'resources.developmentOverviewTile.title',
    defaultMessage: 'Development overview'
  },
  developmentOverviewTileText: {
    id: 'resources.developmentOverviewTile.text',
    defaultMessage:
      'Learn about how web chat works and discover the different ways you can use web chat’s extensibility to deliver powerful, seamless experiences for your users.'
  },
  downloadableTranscriptTileTitle: {
    id: 'resources.downloadableTranscriptTile.title',
    defaultMessage: 'Providing a downloadable transcript'
  },
  downloadableTranscriptTileText: {
    id: 'resources.downloadableTranscriptTile.text',
    defaultMessage:
      'Use this tutorial if you’d like to offer your users the option of downloading a transcript of their conversation history.'
  },
  implementingCustomButtonsTileTitle: {
    id: 'resources.implementingCustomButtonsTile.title',
    defaultMessage: 'Implementing custom buttons.'
  },
  implementingCustomButtonsTileText: {
    id: 'resources.implementingCustomButtonsTile.text',
    defaultMessage:
      'Use this tutorial if you’d like to replace the styling of the standard Options response type with custom button styling.'
  },
  administrationSectionTitle: {
    id: 'resources.administrationSection.title',
    defaultMessage: 'Administration'
  },
  administrationSectionSubTitle: {
    id: 'resources.administrationSection.subtitle',
    defaultMessage: 'Make web chat work for you and your users'
  },
  supportingGlobalAudiencesTileTitle: {
    id: 'resources.supportingGlobalAudiencesTile.title',
    defaultMessage: 'Supporting global audiences'
  },
  supportingGlobalAudiencesTileText: {
    id: 'resources.supportingGlobalAudiencesTile.text',
    defaultMessage:
      'Set web chat’s locale to change your default responses to another language.'
  },
  versioningTileTitle: {
    id: 'resources.versioningTile.title',
    defaultMessage: 'Versioning'
  },
  versioningTileText: {
    id: 'resources.versioningTile.text',
    defaultMessage:
      'Receive updates automatically or lock your version and update on your schedule.'
  },
  securityTileTitle: {
    id: 'resources.securityTile.title',
    defaultMessage: 'Security'
  },
  securityTileText: {
    id: 'resources.securityTile.text',
    defaultMessage:
      'Secure your web chat and learn how to set up user authentication to enable access to your existing security login portal (e.g. Single Sign-On).'
  },
  userIdentityTileTitle: {
    id: 'resources.userIdentityTile.title',
    defaultMessage: 'User identity'
  },
  userIdentityTileText: {
    id: 'resources.userIdentityTile.text',
    defaultMessage:
      'If you don’t enable security but want to perform tasks where you need to identify the user, you’ll need to pass the user ID to the web chat integration.'
  },
  docsTagTitle: {
    id: 'resources.docsTagTitle',
    defaultMessage: 'Docs'
  },
  tutorialTagTitle: {
    id: 'resources.tutorialTagTitle',
    defaultMessage: 'Tutorial'
  },
  demoSiteTagTitle: {
    id: 'resources.demoSiteTagTitle',
    defaultMessage: 'Demo site'
  },
  addingWebchatToMobileTileTitle: {
    id: 'resources.addingWebchatToMobileTileTitle',
    defaultMessage: 'Adding the web chat to your mobile application'
  },
  addingWebchatToMobileTileText: {
    id: 'resources.addingWebchatToMobileTileText',
    defaultMessage: 'Learn how to deploy web chat in your mobile apps'
  },
  lendyrWebchatEssentialsTileTitle: {
    id: 'resources.lendyrWebchatEssentialsTileTitle',
    defaultMessage: 'Web chat essentials feature tour'
  },
  showingContentCarouselTileTitle: {
    id: 'resources.showingContentCarouselTileTitle',
    defaultMessage: 'Showing a content carousel'
  },
  showingContentCarouselTileText: {
    id: 'resources.showingContentCarouselTileText',
    defaultMessage:
      'Use this tutorial to learn how to render information in the form of a content carousel'
  },
  lendyrJourneysTileTitle: {
    id: 'resources.lendyrJourneysTileTitle',
    defaultMessage: 'Journeys'
  },
  lendyrJourneysTileText: {
    id: 'resources.lendyrJourneysTileText',
    defaultMessage:
      'View this demo to learn how {productName} can guide your users through adopting features or completing tasks while using existing capabilities on your website'
  },
  settingContextVarTileTitle: {
    id: 'resources.settingContextVarTileTitle',
    defaultMessage: 'Setting context variables'
  },
  settingContextVarTileText: {
    id: 'resources.settingContextVarTileText',
    defaultMessage:
      'Use this tutorial to learn how to have the web chat set variables (e.g. whether user is logged in) that your conversations can then condition on and use'
  },
  interactingWithWebsiteTileTitle: {
    id: 'resources.interactingWithWebsiteTileTitle',
    defaultMessage: 'Interacting with website'
  },
  interactingWithWebsiteTileText: {
    id: 'resources.interactingWithWebsiteTileText',
    defaultMessage:
      'Use this tutorial to learn how to use custom responses and events to enable the web chat to interact with the web page where it appears'
  },
  deepLinkingTileTitle: {
    id: 'resources.deepLinkingTileTitle',
    defaultMessage: 'Deep Linking'
  },
  deepLinkingTileText: {
    id: 'resources.deepLinkingTileText',
    defaultMessage:
      'View this demo to learn how deep linking can be used to direct customers from external applications (like emails) to your assistant, preconfigured to help the user with a specific intent'
  },
  webchaThemingTileTitle: {
    id: 'resources.webchaThemingTileTitle',
    defaultMessage: 'Theming'
  },
  webchaThemingTileText: {
    id: 'resources.webchaThemingTileText',
    defaultMessage:
      'View this demo to learn how web chat can be extensively themed to match your brand'
  }
});
